import { WarningOutlined } from '@ant-design/icons'
import { type TableColumnsType, Tooltip } from 'antd'

import { formatAmount, formatCount, formatShare } from '@/utils/count'

import { type CapacityRow, type IpRow, soldShare } from './capacitySummary'
import { ipUsageSegments, usageSegments } from './capacityTotals'
import { UsageBar } from './UsageBar'

function SoldAmount({ row }: { row: CapacityRow }) {
  if (!row.note) return <>{formatAmount(row.sold)}</>
  return (
    <Tooltip title={row.note} trigger={['hover', 'focus']}>
      <span tabIndex={0}>
        {formatAmount(row.sold)} <WarningOutlined aria-label={row.note} />
      </span>
    </Tooltip>
  )
}

const amountColumn = (title: string, key: keyof CapacityRow) => ({
  title,
  dataIndex: key,
  align: 'right' as const,
  render: (value?: number) => formatAmount(value),
})

export const capacityColumns: TableColumnsType<CapacityRow> = [
  { title: 'Resource', dataIndex: 'resource' },
  amountColumn('Total', 'total'),
  amountColumn('Allocatable', 'allocatable'),
  {
    title: 'Sold',
    key: 'sold',
    align: 'right',
    render: (_, row) => <SoldAmount row={row} />,
  },
  amountColumn('Internal groups', 'internal'),
  amountColumn('Platform pods', 'platformPods'),
  amountColumn('Free', 'free'),
  amountColumn('Physically free', 'physicalFree'),
  {
    title: 'Sold share',
    key: 'share',
    align: 'right',
    render: (_, row) => formatShare(soldShare(row)),
  },
  {
    title: 'Usage',
    key: 'usage',
    width: 180,
    render: (_, row) => (
      <UsageBar label={row.resource} segments={usageSegments(row)} />
    ),
  },
]

const countColumn = (
  title: string,
  key: 'total' | 'sold' | 'platform' | 'free',
) => ({
  title,
  dataIndex: key,
  align: 'right' as const,
  render: (value: bigint) => (
    <span title={value.toLocaleString('en-US')}>{formatCount(value)}</span>
  ),
})

export const ipColumns: TableColumnsType<IpRow> = [
  { title: 'Public IPs', dataIndex: 'family' },
  countColumn('Total', 'total'),
  countColumn('Sold', 'sold'),
  countColumn('Platform', 'platform'),
  countColumn('Free', 'free'),
  {
    title: 'Usage',
    key: 'usage',
    width: 180,
    render: (_, row) =>
      row.family === 'V4' ? (
        <UsageBar label="Public IPv4" segments={ipUsageSegments(row)} />
      ) : null,
  },
]
